import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Shield, Users, MapPin, Globe, Sparkles, ArrowRight } from "lucide-react";

const PRESETS = [10, 25, 50, 100, 250, 500, 1000];

function DonationCta() {
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const useCustom = custom !== "";
  const finalAmount = useCustom ? Number(custom) : amount;

  const donate = async () => {
    if (!finalAmount || finalAmount < 1) {
      toast.error("Please enter a donation amount of at least $1");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/donations/checkout", {
        amount: finalAmount,
        origin_url: window.location.origin,
        donor_name: name || undefined,
        donor_email: email || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div data-testid="donation-cta" className="wofy-card">
      <div className="overline">MAKE A DONATION NOW</div>
      <h3 className="font-display text-2xl mt-1">Choose an amount</h3>

      <div className="mt-5 grid grid-cols-4 sm:grid-cols-7 gap-2">
        {PRESETS.map((p) => (
          <button
            key={p}
            data-testid={`donate-amount-${p}`}
            onClick={() => { setAmount(p); setCustom(""); }}
            className={`py-3 rounded-lg text-sm font-medium transition-all ${!useCustom && amount === p ? "text-white" : ""}`}
            style={!useCustom && amount === p
              ? { background: "var(--wofy-primary)" }
              : { border: "1px solid var(--wofy-border)" }}
          >
            ${p}
          </button>
        ))}
      </div>

      <div className="mt-3">
        <label className="text-xs font-mono uppercase tracking-wider">Or enter a custom amount</label>
        <div className="relative mt-2">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>$</span>
          <input
            data-testid="donate-custom-amount"
            type="number"
            min="1"
            step="1"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Custom amount"
            className="w-full rounded-lg border pl-8 pr-4 py-3 bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          />
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Name (optional)</span>
          <input data-testid="donate-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Email (optional)</span>
          <input data-testid="donate-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
      </div>

      <button
        onClick={donate}
        disabled={busy}
        data-testid="donate-submit"
        className="wofy-btn-primary w-full justify-center mt-6 disabled:opacity-60"
      >
        {busy ? "Redirecting to Stripe…" : (
          <>
            <Heart className="w-4 h-4" /> Donate ${finalAmount || 0} securely with Stripe
          </>
        )}
      </button>
      <p className="mt-3 text-[11px] text-center" style={{ color: "var(--wofy-text-soft)" }}>
        Secure checkout powered by Stripe. Card details never touch our servers.
      </p>
    </div>
  );
}

function Section({ icon: Ic, title, items }) {
  return (
    <section className="mt-10">
      <div className="flex items-center gap-3">
        <Ic className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
        <h2 className="font-display text-2xl">{title}</h2>
      </div>
      <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        {items.map((it) => <li key={it} className="pl-4 relative before:content-['•'] before:absolute before:left-0" style={{ color: "var(--wofy-text)" }}>{it}</li>)}
      </ul>
    </section>
  );
}

function TestModeBanner() {
  const [mode, setMode] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await http.get("/donations/mode"); setMode(data.mode); }
      catch { setMode(null); }
    })();
  }, []);
  if (mode !== "test") return null;
  return (
    <div
      data-testid="donate-test-mode-banner"
      className="mb-4 rounded-lg border p-4 flex items-start gap-3"
      style={{ background: "#FEF3C7", borderColor: "#F59E0B", color: "#78350F" }}
    >
      <span className="text-2xl leading-none">⚠️</span>
      <div className="min-w-0">
        <div className="font-semibold text-base">TEST MODE — no real payments will be charged</div>
        <p className="text-sm mt-1 opacity-90">
          Our payment system is currently in Stripe test mode. Any donation you enter here will
          <b> not charge your card</b> and does not create a real donation record. If you'd like to
          make a real donation, please email us at <a href="mailto:hello@socialpulse.com" className="underline">hello@socialpulse.com</a> —
          we're switching to live mode shortly.
        </p>
      </div>
    </div>
  );
}

export default function Donate() {
  return (
    <div data-testid="donate-page" className="max-w-[820px] mx-auto px-6 lg:px-10 py-16">
      <TestModeBanner />
      <div className="overline mb-3">SUPPORT THE MISSION</div>
      <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">
        Support Social Pulse Global Initiatives — Make a Donation
      </h1>
      <p className="mt-4 text-lg italic" style={{ color: "var(--wofy-text-soft)" }}>
        Your donation creates real change.
      </p>

      <div className="mt-8 space-y-5 text-base leading-relaxed">
        <p>Every act of kindness begins with someone who cares.</p>
        <p>
          At Social Pulse, our mission is simple: to connect people, communities, businesses, and organizations so that together we can improve lives through housing, food security, education, healthcare, environmental stewardship, disaster relief, and many other humanitarian initiatives.
        </p>
        <p>
          Whether it's helping a family rebuild their home after a disaster, providing nutritious meals to children, supporting educational opportunities, assisting seniors, or connecting volunteers with community projects, every program begins with one essential ingredient — <b>people who believe in making a difference</b>.
        </p>
        <p>Your donation helps turn compassion into action.</p>
      </div>

      {/* Top donation CTA */}
      <div className="mt-10">
        <a href="#donate-form" data-testid="donate-top-link" className="wofy-btn-primary">
          <Heart className="w-4 h-4" /> Make a Donation Now <ArrowRight className="w-4 h-4" />
        </a>
      </div>

      {/* Editorial body */}
      <div className="mt-14 space-y-5 text-base leading-relaxed">
        <h2 className="font-display text-3xl">More Than a Donation</h2>
        <p>When you support Social Pulse, you are investing in a platform designed to connect those who need help with those who are willing to help.</p>
        <p><b>Every dollar helps us:</b></p>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm" style={{ color: "var(--wofy-text)" }}>
          <li>Connect volunteers with meaningful opportunities.</li>
          <li>Build partnerships with charities and businesses.</li>
          <li>Develop innovative solutions that strengthen communities.</li>
          <li>Expand our reach to underserved populations.</li>
          <li>Create lasting, sustainable change.</li>
        </ul>
        <p>Your generosity helps multiply kindness around the world.</p>

        <h2 className="font-display text-3xl mt-10">Why Operational Support Matters</h2>
        <p>Many people prefer donating directly to programs, and we understand why. However, every successful nonprofit depends on a strong foundation behind the scenes.</p>
        <p>Just as a hospital needs doctors, nurses, computers, and electricity to care for patients, Social Pulse needs reliable technology, dedicated staff, and secure systems to deliver our services effectively.</p>
        <p><b>Your support helps us maintain and improve:</b></p>
      </div>

      <Section
        icon={Sparkles}
        title="Technology Infrastructure"
        items={["Website development and maintenance", "Mobile applications", "Cloud hosting", "Databases", "Software development", "System monitoring", "Data backups", "Accessibility improvements", "User support"]}
      />
      <Section
        icon={Shield}
        title="Security and Privacy"
        items={["Cybersecurity systems", "Fraud prevention", "Secure payment processing", "Data protection", "Privacy compliance", "Regular security updates", "System monitoring"]}
      />
      <p className="mt-3 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>A secure platform protects everyone who uses our services.</p>

      <Section
        icon={Users}
        title="Staff and Volunteers"
        items={["Program coordinators", "Technical support", "Community outreach", "Volunteer coordination", "Administrative staff", "Customer support", "Communications", "Project management"]}
      />
      <p className="mt-3 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>Our team works to ensure that donations, volunteers, and community resources create the greatest possible impact.</p>

      <Section
        icon={Globe}
        title="Community Outreach"
        items={["Community education", "Awareness campaigns", "Volunteer recruitment", "Partnership development", "Public events", "Educational materials", "Community workshops"]}
      />
      <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        The more people who know about <Link to="/vision-mission" className="underline" style={{ color: "var(--wofy-primary)" }}>Social Pulse</Link>, the more lives we can touch.
      </p>

      <Section
        icon={MapPin}
        title="Travel and Field Operations"
        items={["Community assessments", "Volunteer projects", "Disaster response", "Partnership meetings", "Training programs", "Site visits", "Program oversight"]}
      />
      <p className="mt-3 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>Being present in the community helps us better understand local needs and build lasting relationships.</p>

      <Section
        icon={Shield}
        title="Governance and Compliance"
        items={["Financial management", "Legal compliance", "Accounting", "Audits", "Insurance", "Regulatory filings", "Organizational governance"]}
      />
      <p className="mt-3 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>These essential functions help ensure that Social Pulse operates responsibly and ethically.</p>

      <div className="mt-14 space-y-5 text-base leading-relaxed">
        <h2 className="font-display text-3xl">Every Gift Matters</h2>
        <p>Whether you donate $10 or $10,000, every contribution helps us continue building a trusted platform that serves communities around the world.</p>
        <p><b>Your support enables us to:</b></p>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <li>Reach more people.</li>
          <li>Launch new humanitarian programs.</li>
          <li>Improve our technology.</li>
          <li>Respond to emergencies.</li>
          <li>Expand volunteer opportunities.</li>
          <li>Build stronger partnerships.</li>
          <li>Serve communities more effectively.</li>
        </ul>
        <p className="italic">Together, small acts of generosity become extraordinary achievements.</p>

        <h2 className="font-display text-3xl mt-10">Ways You Can Help</h2>
        <p>There are many ways to support Social Pulse:</p>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <li>Make a one-time donation.</li>
          <li>Become a monthly supporter.</li>
          <li>Sponsor a specific project.</li>
          <li>Donate in memory or honor of a loved one.</li>
          <li>Encourage employer matching gifts.</li>
          <li>Volunteer your time and expertise.</li>
          <li>Partner as a business or nonprofit.</li>
          <li>Share our mission with family and friends.</li>
          <li>Donate professional services or technology.</li>
          <li>Leave a legacy gift for future generations.</li>
        </ul>
        <p className="italic">Every contribution, regardless of size, helps move our mission forward.</p>

        <h2 className="font-display text-3xl mt-10">Our Commitment to Stewardship</h2>
        <p>We recognize that every donation represents trust.</p>
        <p>Social Pulse is committed to using donations responsibly, making thoughtful decisions, and continually improving the effectiveness of our programs and operations. We strive to be transparent, accountable, and focused on creating meaningful, measurable impact for the communities we serve.</p>

        <h2 className="font-display text-3xl mt-10">Together We Can Build a Better World</h2>
        <p>A better world is not created by one organization alone. It is built by millions of people choosing kindness, generosity, and action.</p>
        <p>Your donation helps provide more than financial support — it helps build hope, strengthen communities, inspire volunteers, develop innovative solutions, and create opportunities for people to improve their lives.</p>
        <p>Together, we can bring more joy, compassion, and opportunity to communities everywhere.</p>
        <p>Thank you for believing that positive change is possible.</p>
        <p><b>Thank you for helping make the world a better place.</b></p>

        <blockquote className="mt-8 border-l-4 pl-5 italic text-lg" style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-text)" }}>
          "Your donation doesn't just keep our website online. It powers a global platform that connects volunteers, nonprofits, businesses, donors, and communities — making it easier for people to find help, offer help, and work together continuously to improve lives."
        </blockquote>
      </div>

      {/* Bottom donation CTA — actual form */}
      <div id="donate-form" className="mt-14 scroll-mt-24">
        <DonationCta />
      </div>
    </div>
  );
}
